/**
 * QR Code Management Helpers
 * Utility functions for formatting, normalization, and calculations
 */

/**
 * Format a date string for display
 */
export function formatDate(date: string | null | undefined): string {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date string with time
 */
export function formatDateTime(date: string | null | undefined): string {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format relative date (e.g., "2 days ago")
 */
export function formatRelativeDate(date: string | null | undefined): string {
  if (!date) return 'N/A';
  const now = new Date();
  const d = new Date(date);
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

/**
 * Check if a QR code is expired
 */
export function isQrCodeExpired(expiresAt: string | null | undefined): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
}

/**
 * Check if a QR code is active (Active status + not expired)
 */
export function isQrCodeActive(status: string, expiresAt?: string | null): boolean {
  if (status !== 'Active') return false;
  if (expiresAt && isQrCodeExpired(expiresAt)) return false;
  return true;
}

/**
 * Format scan count for display
 */
export function formatScanCount(count: number): string {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Generate a display label for a QR code type
 */
export function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    URL: 'Website URL',
    vCard: 'Contact Card',
    WiFi: 'WiFi Network',
    Email: 'Email',
    SMS: 'SMS Message',
    Phone: 'Phone Number',
    Text: 'Plain Text',
    Event: 'Calendar Event',
    Location: 'Location',
    AppStore: 'App Store',
  };
  return labels[type] || type;
}

/**
 * Truncate text to a maximum length with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}
